import { KeyRound, LifeBuoy } from "lucide-react";
import { type FormEvent, useState } from "react";

import { api } from "../api/client";
import type { AuthSession, LicenceStatus } from "../api/types";
import { attentionSelector, designate } from "../components/attention";
import { Button, Field, inputClass } from "../components/ui";
import { t } from "../i18n/i18n";
import { LicenceFrame } from "../licence/ActivationScreen";
import { AuthTitle, codeComplete, CodeInput, FormError, NewPasswordFields } from "./AuthForm";
import { type AuthContext, authErrorText, passwordProblem } from "./authText";
import { seatSubtitle, useSignIn } from "./LoginScreen";
import { Waiting } from "../components/Waiting";

/** Formulaire commun « identifiant + code + nouveau mot de passe » : code d'activation ou de réinitialisation
 *  (`redeem`, §5.3, §5.4), code de secours de l'unité (`recover`, §5.5). */
function CodeForm({
  context,
  session,
  licence,
  onDone,
  onBack,
}: {
  context: Extract<AuthContext, "redeem" | "recover">;
  session: AuthSession;
  licence: LicenceStatus;
  onDone: (result: AuthSession) => void;
  onBack: () => void;
}) {
  const [login, setLogin] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const send = useSignIn(() =>
    context === "redeem" ? api.auth.redeem(login.trim(), code, password) : api.auth.recover(login.trim(), code, password),
  );
  const missing = !login.trim()
    ? "auth-login"
    : !codeComplete(code)
      ? "auth-code"
      : passwordProblem(password, confirm)
        ? password.length < 12
          ? "auth-new-password"
          : "auth-confirm-password"
        : null;
  const blocked = () => {
    if (!missing) return;
    if (missing === "auth-new-password" || missing === "auth-confirm-password") setProblem(passwordProblem(password, confirm));
    designate(attentionSelector(missing));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (missing) return blocked();
    if (send.isPending) return;
    setProblem(null);
    send.mutate(undefined, {
      onSuccess: (result) => {
        setPassword("");
        setConfirm("");
        onDone(result);
      },
      onError: (error) => setProblem(authErrorText(error, context)),
    });
  };
  const redeem = context === "redeem";
  return (
    <LicenceFrame>
      <AuthTitle icon={redeem ? <KeyRound size={16} /> : <LifeBuoy size={16} />}>
        {redeem ? t("Choisir votre mot de passe") : t("Reprendre la main sur un compte administrateur")}
      </AuthTitle>
      <p className="mt-1 text-ui text-ink-soft">{seatSubtitle(session, licence)}</p>
      <p className="mt-1.5 text-ui leading-relaxed text-ink-soft">
        {redeem
          ? t("Saisissez votre identifiant et le code remis par l'administrateur de votre unité, puis choisissez votre mot de passe. Le code ne sert qu'une fois.")
          : t("Dernier recours, quand aucun autre administrateur de l'unité n'est joignable : saisissez votre identifiant d'administrateur et le code de secours de l'unité, puis choisissez un nouveau mot de passe. Le code de secours ne sert qu'une fois : un nouveau vous sera remis.")}
      </p>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <Field label={redeem ? t("Identifiant") : t("Identifiant de l'administrateur")}>
          <input
            data-attention="auth-login"
            className={inputClass}
            value={login}
            maxLength={40}
            onChange={(e) => setLogin(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
          />
        </Field>
        <Field label={redeem ? t("Code (XXXX-XXXX-XXXX-XXXX)") : t("Code de secours (XXXX-XXXX-XXXX-XXXX)")}>
          <CodeInput value={code} onChange={setCode} attention="auth-code" />
        </Field>
        <NewPasswordFields password={password} confirm={confirm} onPassword={setPassword} onConfirm={setConfirm} />
        <FormError message={problem} />
        <Button type="submit" className="w-full" disabled={send.isPending} blocked={Boolean(missing)} onBlocked={blocked}>
          {send.isPending ? <Waiting>{t("Enregistrement…")}</Waiting> : redeem ? t("Enregistrer et se connecter") : t("Reprendre la main")}
        </Button>
      </form>
      <Button variant="link" className="mt-1 py-2.5" onClick={onBack}>
        {t("Retour à la connexion")}
      </Button>
    </LicenceFrame>
  );
}

/** Choisir votre mot de passe, avec un code d'activation ou de réinitialisation (§8.3). */
export function RedeemScreen(props: { session: AuthSession; licence: LicenceStatus; onDone: (result: AuthSession) => void; onBack: () => void }) {
  return <CodeForm context="redeem" {...props} />;
}

/** Reprendre la main sur un compte administrateur avec le code de secours de l'unité (§5.5, §8.3) ; la réussite rend
 *  un nouveau code de secours, montré aussitôt. */
export function RecoverScreen(props: { session: AuthSession; licence: LicenceStatus; onDone: (result: AuthSession) => void; onBack: () => void }) {
  return <CodeForm context="recover" {...props} />;
}
